import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CustomerTypeahead } from '@/components/ui/customer-typeahead';
import { AddressInput, type AddressData } from '@/components/ui/address-input';
import {
  listGasStations,
  listProducts,
  listPaymentClients,
  createOrder,
  createOrderWithNewCustomer,
  type UserOrganization,
  type PaymentClientSummary,
} from '@vn-propane/shared/client';
import type { Customer, GasStation, Product, ErrorResponse, CreateOrderItem, PaymentMethod } from '@vn-propane/shared/client';
import { ArrowLeft, ArrowRight, Store, User, Package, Check, Minus, Plus, Loader2, MapPin, CreditCard, Banknote } from 'lucide-react';
import { cn, formatPrice } from '@/lib/utils';

type OutletContext = {
  organization: UserOrganization;
};

type Step = 'station' | 'customer' | 'products' | 'review';

interface NewCustomerForm {
  fullName: string;
  phone: string;
  addressData: AddressData | null;
}

interface OrderItem {
  product: Product;
  quantity: number;
}

export default function NewOrder() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OutletContext>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('station');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [gasStations, setGasStations] = useState<GasStation[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  // Form state
  const [selectedStation, setSelectedStation] = useState<GasStation | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState<NewCustomerForm>({
    fullName: '',
    phone: '',
    addressData: null,
  });
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [hasOnlinePayment, setHasOnlinePayment] = useState(false);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!orgId) return;

      setLoading(true);
      try {
        const [stationsResult, productsResult, paymentResult] = await Promise.all([
          listGasStations({ path: { org_id: orgId } }),
          listProducts({ path: { org_id: orgId } }),
          listPaymentClients({ path: { org_id: orgId } }),
        ]);

        if (stationsResult.data) {
          setGasStations(stationsResult.data.data);
        }
        if (productsResult.data) {
          setProducts(productsResult.data.data);
          // Initialize order items with 0 quantity
          setOrderItems(productsResult.data.data.map(p => ({ product: p, quantity: 0 })));
        }
        if (paymentResult.data) {
          const enabledClients = paymentResult.data.data.filter((c: PaymentClientSummary) => c.isEnabled);
          const hasOnline = enabledClients.length > 0;
          setHasOnlinePayment(hasOnline);
          setPaymentMethod(hasOnline ? 'online' : 'cash');
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(t('orders.failedToLoadFormData'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId]);

  const updateQuantity = (productId: string, delta: number) => {
    setOrderItems(items =>
      items.map(item =>
        item.product.id === productId
          ? { ...item, quantity: Math.max(0, item.quantity + delta) }
          : item
      )
    );
  };

  const setQuantity = (productId: string, quantity: number) => {
    setOrderItems(items =>
      items.map(item =>
        item.product.id === productId
          ? { ...item, quantity: Math.max(0, quantity) }
          : item
      )
    );
  };

  const selectedItems = orderItems.filter(item => item.quantity > 0);

  const totalAmount = selectedItems.reduce((sum, item) => {
    const price = parseFloat(item.product.price || '0');
    return sum + price * item.quantity;
  }, 0);

  const canProceed = (): boolean => {
    switch (step) {
      case 'station':
        return selectedStation !== null;
      case 'customer':
        return selectedCustomer !== null || (isCreatingCustomer && newCustomer.fullName.trim() !== '');
      case 'products':
        return selectedItems.length > 0;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const goToNext = () => {
    const steps: Step[] = ['station', 'customer', 'products', 'review'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex < steps.length - 1) {
      setStep(steps[currentIndex + 1]);
    }
  };

  const goToPrev = () => {
    const steps: Step[] = ['station', 'customer', 'products', 'review'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1]);
    }
  };

  const handleSubmit = async () => {
    if (!orgId || !selectedStation) return;

    setSubmitting(true);
    setError(null);

    try {
      const items: CreateOrderItem[] = selectedItems.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
      }));

      let result;

      if (isCreatingCustomer) {
        result = await createOrderWithNewCustomer({
          path: { org_id: orgId },
          body: {
            gas_station_id: selectedStation.id,
            customer: {
              fullName: newCustomer.fullName,
              phone: newCustomer.phone || null,
              address: newCustomer.addressData?.createRequest,
            },
            items,
            notes: notes || null,
            payment_method: paymentMethod,
          },
        });
      } else if (selectedCustomer) {
        result = await createOrder({
          path: { org_id: orgId },
          body: {
            gas_station_id: selectedStation.id,
            customer_id: selectedCustomer.id,
            items,
            notes: notes || null,
            payment_method: paymentMethod,
          },
        });
      } else {
        setError(t('orders.pleaseSelectCustomer'));
        setSubmitting(false);
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('orders.failedToCreateOrder'));
        return;
      }

      if (result.data) {
        navigate(`/organizations/${orgId}/orders/${result.data.data.id}`);
      }
    } catch (err) {
      setError(t('orders.failedToCreateOrder'));
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  const stepIndicator = (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[
        { key: 'station', icon: Store, label: t('orders.stepStation') },
        { key: 'customer', icon: User, label: t('orders.stepCustomer') },
        { key: 'products', icon: Package, label: t('orders.stepProducts') },
        { key: 'review', icon: Check, label: t('orders.stepReview') },
      ].map((s, i, arr) => {
        const isActive = s.key === step;
        const isPast = arr.findIndex(x => x.key === step) > i;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex items-center">
            <div
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-full',
                isActive ? 'bg-primary text-primary-foreground' :
                isPast ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
              )}
            >
              <Icon className="w-4 h-4" />
            </div>
            <span className={cn(
              'ml-2 text-sm hidden sm:inline',
              isActive ? 'font-medium' : 'text-muted-foreground'
            )}>
              {s.label}
            </span>
            {i < arr.length - 1 && (
              <div className={cn(
                'w-8 h-0.5 mx-2',
                isPast ? 'bg-primary' : 'bg-muted'
              )} />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/organizations/${orgId}/orders`}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('orders.title')}
          </Link>
        </Button>
      </div>

      <h2 className="text-2xl font-extrabold tracking-tight mb-2">{t('orders.newOrder')}</h2>
      <p className="text-muted-foreground mb-6">{t('orders.createNewOrderDescription')}</p>

      {stepIndicator}

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Step 1: Select Gas Station */}
      {step === 'station' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('orders.selectGasStation')}</CardTitle>
            <CardDescription>{t('orders.selectStationDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {gasStations.length === 0 ? (
              <p className="text-muted-foreground">{t('orders.noGasStationsAvailable')}</p>
            ) : (
              gasStations.map(station => (
                <button
                  key={station.id}
                  type="button"
                  onClick={() => setSelectedStation(station)}
                  className={cn(
                    'w-full p-3 text-left rounded-md border transition-colors',
                    selectedStation?.id === station.id
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-primary/50'
                  )}
                >
                  <div className="font-medium">{station.name}</div>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select or Create Customer */}
      {step === 'customer' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('orders.customer')}</CardTitle>
            <CardDescription>{t('orders.customerStepDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isCreatingCustomer ? (
              <>
                <CustomerTypeahead
                  organizationId={orgId!}
                  value={selectedCustomer}
                  onChange={setSelectedCustomer}
                  onCreateNew={() => setIsCreatingCustomer(true)}
                />
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">{t('orders.newCustomer')}</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsCreatingCustomer(false);
                      setNewCustomer({
                        fullName: '',
                        phone: '',
                        addressData: null,
                      });
                    }}
                  >
                    {t('orders.searchInstead')}
                  </Button>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label htmlFor="fullName">{t('customers.fullNameRequired')}</Label>
                    <Input
                      id="fullName"
                      value={newCustomer.fullName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, fullName: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('customers.phone')}</Label>
                    <Input
                      id="phone"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    />
                  </div>

                  {/* Address input */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <Label className="text-sm font-medium">{t('addresses.deliveryAddress')}</Label>
                    </div>
                    <AddressInput
                      organizationId={orgId!}
                      value={newCustomer.addressData}
                      onChange={(data) => setNewCustomer({ ...newCustomer, addressData: data })}
                      deferCreation
                      defaultCountry="VN"
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Select Products */}
      {step === 'products' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('orders.products')}</CardTitle>
            <CardDescription>{t('orders.productsStepDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {products.length === 0 ? (
              <p className="text-muted-foreground">{t('orders.noProductsAvailable')}</p>
            ) : (
              <div className="space-y-3">
                {orderItems.map(item => (
                  <div
                    key={item.product.id}
                    className={cn(
                      'p-3 border rounded-md',
                      item.quantity > 0 && 'border-primary bg-primary/5'
                    )}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium">{item.product.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatPrice(item.product.price, organization.currency)} / {item.product.unit}
                        </div>
                      </div>
                      {item.quantity > 0 && (
                        <div className="text-right">
                          <div className="font-medium">
                            {formatPrice((parseFloat(item.product.price || '0') * item.quantity).toString(), organization.currency)}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => updateQuantity(item.product.id, -1)}
                        disabled={item.quantity === 0}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => setQuantity(item.product.id, parseInt(e.target.value) || 0)}
                        className="w-20 text-center"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => updateQuantity(item.product.id, 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedItems.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('orders.total')}</span>
                  <span>{formatPrice(totalAmount.toString(), organization.currency)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review */}
      {step === 'review' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('orders.reviewOrder')}</CardTitle>
              <CardDescription>{t('orders.reviewOrderDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">{t('orders.gasStation')}</Label>
                <p className="font-medium">{selectedStation?.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('orders.customer')}</Label>
                <p className="font-medium">
                  {isCreatingCustomer ? newCustomer.fullName : selectedCustomer?.fullName}
                  {isCreatingCustomer && (
                    <span className="ml-2 text-xs text-muted-foreground">({t('orders.new')})</span>
                  )}
                </p>
                {isCreatingCustomer && newCustomer.addressData && (
                  <p className="text-sm text-muted-foreground mt-1 flex items-start gap-1">
                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{newCustomer.addressData.formattedAddress}</span>
                  </p>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground">{t('orders.items')}</Label>
                <div className="mt-1 divide-y">
                  {selectedItems.map(item => (
                    <div key={item.product.id} className="py-2 flex justify-between">
                      <span>
                        {item.product.name} x {item.quantity}
                      </span>
                      <span className="font-medium">
                        {formatPrice((parseFloat(item.product.price || '0') * item.quantity).toString(), organization.currency)}
                      </span>
                    </div>
                  ))}
                  <div className="py-2 flex justify-between font-bold">
                    <span>{t('orders.total')}</span>
                    <span>{formatPrice(totalAmount.toString(), organization.currency)}</span>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('orders.paymentMethod')}</Label>
                <div className="mt-2 space-y-2">
                  <button
                    type="button"
                    onClick={() => hasOnlinePayment && setPaymentMethod('online')}
                    disabled={!hasOnlinePayment}
                    className={cn(
                      'w-full p-3 rounded-md border text-left transition-colors flex items-center gap-3',
                      !hasOnlinePayment
                        ? 'opacity-50 cursor-not-allowed'
                        : paymentMethod === 'online'
                          ? 'border-primary bg-primary/5'
                          : 'hover:border-primary/50'
                    )}
                  >
                    <CreditCard className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="font-medium">{t('orders.onlinePayment')}</p>
                      <p className="text-sm text-muted-foreground">
                        {hasOnlinePayment ? t('orders.onlinePaymentDescription') : t('orders.noPaymentProcessor')}
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('cash')}
                    className={cn(
                      'w-full p-3 rounded-md border text-left transition-colors flex items-center gap-3',
                      paymentMethod === 'cash'
                        ? 'border-primary bg-primary/5'
                        : 'hover:border-primary/50'
                    )}
                  >
                    <Banknote className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="font-medium">{t('orders.cashOnDelivery')}</p>
                      <p className="text-sm text-muted-foreground">{t('orders.cashOnDeliveryDescription')}</p>
                    </div>
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="notes">{t('orders.notesOptional')}</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('orders.notesPlaceholder')}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={goToPrev}
          disabled={step === 'station'}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common:buttons.back')}
        </Button>

        {step === 'review' ? (
          <Button onClick={handleSubmit} disabled={submitting || !canProceed()}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('orders.creating')}
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {t('orders.createOrder')}
              </>
            )}
          </Button>
        ) : (
          <Button onClick={goToNext} disabled={!canProceed()}>
            {t('common:buttons.next')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
